import { useState, useRef, useEffect } from "react";
import { uploadFile } from "../api/storage";
import { parseResume, parseResumeDirect, applyResume } from "../api/resume";
import { C, font } from "../styles/tokens";
import { LiquidLoader } from "./LiquidLoader";

export function ResumeUploadModal({ isOpen, onClose, onApplied, currentProfile }) {
  const [step, setStep] = useState("UPLOAD"); // 'UPLOAD' | 'PROCESSING' | 'REVIEW' | 'SUCCESS'
  const [isDragging, setIsDragging] = useState(false);
  const [processingMsg, setProcessingMsg] = useState("Uploading resume document...");
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("summary"); // 'summary' | 'skills' | 'experience' | 'education' | 'certifications'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [appliedStats, setAppliedStats] = useState(null);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const modalRef = useRef(null);

  // Review Draft State
  const [fileName, setFileName] = useState("");
  const [confidence, setConfidence] = useState({ contact: 0.9, skills: 0.9, experience: 0.8, education: 0.8 });
  const [bio, setBio] = useState("");
  const [applyBio, setApplyBio] = useState(true);
  const [city, setCity] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [applyLocation, setApplyLocation] = useState(true);
  const [skills, setSkills] = useState([]);
  const [mergeSkills, setMergeSkills] = useState(true);
  const [skillInput, setSkillInput] = useState("");
  const [experience, setExperience] = useState([]);
  const [education, setEducation] = useState([]);
  const [certifications, setCertifications] = useState([]);

  // Close on ESC
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape" && isOpen && !isSubmitting) {
        onClose();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isSubmitting, onClose]);

  // Reset when opened
  useEffect(() => {
    if (isOpen) {
      setStep("UPLOAD");
      setError(null);
      setIsSubmitting(false);
      setAppliedStats(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // ---------------------------------------------------------------------------
  // File Upload & Parsing Handler
  // ---------------------------------------------------------------------------
  async function handleFileSelected(file) {
    if (!file) return;

    const MAX_BYTES = 5 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      setError("File exceeds maximum allowed size of 5 MB. Please select a smaller document.");
      return;
    }

    const ALLOWED_EXTS = [".pdf", ".docx", ".doc", ".jpg", ".jpeg", ".png"];
    const ext = file.name ? file.name.slice(file.name.lastIndexOf(".")).toLowerCase() : "";
    if (!ALLOWED_EXTS.includes(ext) && !file.type.includes("pdf") && !file.type.includes("image") && !file.type.includes("word")) {
      setError("Unsupported format. Please upload a PDF, DOCX, DOC, JPG, or PNG resume.");
      return;
    }

    setError(null);
    setStep("PROCESSING");
    setProcessingMsg("Uploading resume document securely...");

    try {
      let parseResult;

      // Primary flow: Presigned URL upload via TickAndPass storage infrastructure
      try {
        setProcessingMsg("Uploading resume to secure storage...");
        const uploadResult = await uploadFile(file, "resume");

        setProcessingMsg("Analyzing document layout & extracting text...");
        parseResult = await parseResume({
          s3Key: uploadResult.s3Key,
          fileName: file.name,
          mimeType: uploadResult.contentType || file.type,
        });
      } catch (storageErr) {
        // Fallback: direct multipart upload if storage endpoint is unreachable
        console.warn("Presigned upload failed, trying direct multipart fallback:", storageErr);
        setProcessingMsg("Processing document with direct upload...");
        parseResult = await parseResumeDirect(file);
      }

      const data = parseResult?.data || parseResult;
      const extracted = data?.extracted;
      if (!extracted) {
        throw new Error(
          data?.message ||
            "Unable to extract information from document. Please ensure the document is clear and readable."
        );
      }
      setFileName(file.name);
      setConfidence(extracted.confidence || { contact: 0.9, skills: 0.9, experience: 0.8, education: 0.8 });

      // Initialize form review state
      setBio(extracted.bio || "");
      setApplyBio(!!extracted.bio);
      setCity(extracted.city || "");
      setStateVal(extracted.state || "");
      setApplyLocation(!!(extracted.city || extracted.state));
      setSkills(extracted.skills || []);
      setMergeSkills(true);

      setExperience(
        (extracted.experience || []).map((exp, idx) => ({
          id: `exp-${idx}-${Date.now()}`,
          selected: true,
          title: exp.title || "",
          issuer: exp.issuer || "",
          department: exp.department || "",
          employmentType: exp.employmentType || "Full time",
          location: exp.location || "",
          startDate: exp.startDate || "",
          endDate: exp.endDate || "",
          isCurrent: !!exp.isCurrent,
          description: exp.description || "",
        }))
      );

      setEducation(
        (extracted.education || []).map((edu, idx) => ({
          id: `edu-${idx}-${Date.now()}`,
          selected: true,
          title: edu.title || "",
          issuer: edu.issuer || "",
          department: edu.department || "",
          grade: edu.grade || "",
          startDate: edu.startDate || "",
          endDate: edu.endDate || "",
          isCurrent: !!edu.isCurrent,
          description: edu.description || "",
        }))
      );

      setCertifications(
        (extracted.certifications || []).map((cert, idx) => ({
          id: `cert-${idx}-${Date.now()}`,
          selected: true,
          title: cert.title || "",
          issuer: cert.issuer || "",
          startDate: cert.startDate || "",
          description: cert.description || "",
        }))
      );

      setStep("REVIEW");
    } catch (err) {
      console.error("Resume processing error:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not extract text from document. Please ensure it is not password protected, or enter your credentials manually."
      );
      setStep("UPLOAD");
    }
  }

  // ---------------------------------------------------------------------------
  // Skills Management
  // ---------------------------------------------------------------------------
  function handleAddSkill() {
    const trimmed = skillInput.trim();
    if (!trimmed) return;
    if (skills.length >= 30) {
      setError("Maximum 30 skills allowed.");
      return;
    }
    if (skills.some((s) => s.toLowerCase() === trimmed.toLowerCase())) {
      setSkillInput("");
      return;
    }
    setSkills([...skills, trimmed]);
    setSkillInput("");
  }

  function handleRemoveSkill(index) {
    setSkills(skills.filter((_, i) => i !== index));
  }

  // ---------------------------------------------------------------------------
  // Experience Handlers
  // ---------------------------------------------------------------------------
  function handleAddExperience() {
    setExperience([
      ...experience,
      {
        id: `exp-new-${Date.now()}`,
        selected: true,
        title: "",
        issuer: "",
        employmentType: "Full time",
        location: "",
        startDate: "",
        endDate: "",
        isCurrent: false,
        description: "",
      },
    ]);
  }

  function handleUpdateExperience(id, field, value) {
    setExperience(
      experience.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  }

  function handleDeleteExperience(id) {
    setExperience(experience.filter((item) => item.id !== id));
  }

  // ---------------------------------------------------------------------------
  // Education Handlers
  // ---------------------------------------------------------------------------
  function handleAddEducation() {
    setEducation([
      ...education,
      {
        id: `edu-new-${Date.now()}`,
        selected: true,
        title: "",
        issuer: "",
        department: "",
        grade: "",
        startDate: "",
        endDate: "",
        isCurrent: false,
        description: "",
      },
    ]);
  }

  function handleUpdateEducation(id, field, value) {
    setEducation(
      education.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  }

  function handleDeleteEducation(id) {
    setEducation(education.filter((item) => item.id !== id));
  }

  // ---------------------------------------------------------------------------
  // Certification Handlers
  // ---------------------------------------------------------------------------
  function handleAddCertification() {
    setCertifications([
      ...certifications,
      {
        id: `cert-new-${Date.now()}`,
        selected: true,
        title: "",
        issuer: "",
        startDate: "",
        description: "",
      },
    ]);
  }

  function handleUpdateCertification(id, field, value) {
    setCertifications(
      certifications.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  }

  function handleDeleteCertification(id) {
    setCertifications(certifications.filter((item) => item.id !== id));
  }

  // ---------------------------------------------------------------------------
  // Apply Review Submission
  // ---------------------------------------------------------------------------
  async function handleSubmitReview() {
    setIsSubmitting(true);
    setError(null);

    const payload = {
      bio: applyBio && bio.trim() ? bio.trim() : undefined,
      city: applyLocation && city.trim() ? city.trim() : undefined,
      state: applyLocation && stateVal.trim() ? stateVal.trim() : undefined,
      skills: skills.length > 0 ? skills : undefined,
      mergeSkills,
      experience: experience
        .filter((e) => e.selected && e.title.trim() && e.issuer.trim())
        .map((e) => ({
          title: e.title.trim(),
          issuer: e.issuer.trim(),
          department: e.department?.trim() || undefined,
          employmentType: e.employmentType?.trim() || undefined,
          location: e.location?.trim() || undefined,
          startDate: e.startDate || undefined,
          endDate: e.isCurrent ? undefined : e.endDate || undefined,
          isCurrent: !!e.isCurrent,
          description: e.description?.trim() || undefined,
        })),
      education: education
        .filter((e) => e.selected && e.title.trim() && e.issuer.trim())
        .map((e) => ({
          title: e.title.trim(),
          issuer: e.issuer.trim(),
          department: e.department?.trim() || undefined,
          grade: e.grade?.trim() || undefined,
          startDate: e.startDate || undefined,
          endDate: e.isCurrent ? undefined : e.endDate || undefined,
          isCurrent: !!e.isCurrent,
          description: e.description?.trim() || undefined,
        })),
      certifications: certifications
        .filter((c) => c.selected && c.title.trim() && c.issuer.trim())
        .map((c) => ({
          title: c.title.trim(),
          issuer: c.issuer.trim(),
          startDate: c.startDate || undefined,
          description: c.description?.trim() || undefined,
        })),
    };

    try {
      const res = await applyResume(payload);
      setAppliedStats(res?.applied || res?.data?.applied || res);
      setStep("SUCCESS");
      if (typeof onApplied === "function") {
        await onApplied();
      }
    } catch (err) {
      console.error("Apply resume error:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save reviewed resume information to your profile."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Render Helper Modals
  // ---------------------------------------------------------------------------
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15, 23, 42, 0.65)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose();
      }}
    >
      <div
        ref={modalRef}
        style={{
          width: "100%",
          maxWidth: 880,
          maxHeight: "92vh",
          background: "#ffffff",
          borderRadius: 16,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: "1px solid #e2e8f0",
          fontFamily: font,
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#ffffff",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "linear-gradient(135deg, #057642 0%, #08833E 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                boxShadow: "0 4px 10px rgba(5, 118, 66, 0.25)",
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
              </svg>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.navy, display: "flex", alignItems: "center", gap: 8 }}>
                Auto-Fill Profile from Resume
                {/* <span style={{ fontSize: 11, fontWeight: 600, color: "#057642", background: "#DCF5E7", padding: "2px 8px", borderRadius: 12 }}>
                  
                </span> */}
              </h2>
              <p style={{ margin: "2px 0 0", fontSize: 12.5, color: C.textMed }}>
                Upload a document or photo of your resume to automatically extract your experience, education & skills.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              fontSize: 16,
              transition: "all 0.15s ease",
            }}
            title="Close"
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {error && (
            <div
              style={{
                marginBottom: 16,
                padding: "12px 16px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 8,
                color: "#991b1b",
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <span>{error}</span>
              <button
                type="button"
                onClick={() => setError(null)}
                style={{ background: "none", border: "none", color: "#991b1b", cursor: "pointer", fontWeight: 700 }}
              >
                ✕
              </button>
            </div>
          )}

          {/* STEP 1: UPLOAD & CAMERA CAPTURE */}
          {step === "UPLOAD" && (
            <div>
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  if (e.dataTransfer.files?.[0]) {
                    handleFileSelected(e.dataTransfer.files[0]);
                  }
                }}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: isDragging ? "2px dashed #057642" : "2px dashed #cbd5e1",
                  background: isDragging ? "#f0fdf4" : "#f8fafc",
                  borderRadius: 14,
                  padding: "36px 20px",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: "#e2e8f0",
                    color: "#057642",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                  }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>

                <div style={{ fontSize: 16, fontWeight: 700, color: C.navy, marginBottom: 6 }}>
                  Drag & drop your resume here, or <span style={{ color: "#057642", textDecoration: "underline" }}>browse files</span>
                </div>
                <div style={{ fontSize: 13, color: C.textMed, marginBottom: 14 }}>
                  Supports PDF, DOCX, DOC, PNG, and JPEG documents up to 5 MB
                </div>

                <div style={{ display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
                  {["PDF Document", "Word (DOC/DOCX)", "Gallery Image (PNG/JPG)"].map((badge) => (
                    <span
                      key={badge}
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#475569",
                        background: "#ffffff",
                        border: "1px solid #cbd5e1",
                        padding: "3px 10px",
                        borderRadius: 20,
                      }}
                    >
                      ✓ {badge}
                    </span>
                  ))}
                </div>
              </div>

              {/* Hidden file inputs */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files?.[0]) handleFileSelected(e.target.files[0]);
                }}
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: "none" }}
                onChange={(e) => {
                  if (e.target.files?.[0]) handleFileSelected(e.target.files[0]);
                }}
              />

              {/* Alternative Mobile Input Action: Camera Capture */}
              <div
                style={{
                  marginTop: 18,
                  padding: "14px 18px",
                  background: "#f1f5f9",
                  borderRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 20 }}>📸</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>
                      Mobile Camera Scan
                    </div>
                    <div style={{ fontSize: 12, color: C.textMed }}>
                      Capture a photo of a physical printed resume or certificate
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  style={{
                    padding: "8px 16px",
                    background: "#ffffff",
                    border: "1px solid #cbd5e1",
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.navy,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  Scan with Camera
                </button>
              </div>

              {/* Privacy and Verification Policy Notice */}
              <div
                // style={{
                //   marginTop: 20,
                //   padding: "12px 14px",
                //   background: "#f0fdf4",
                //   border: "1px solid #bbf7d0",
                //   borderRadius: 10,
                //   fontSize: 12,
                //   color: "#166534",
                //   lineHeight: 1.5,
                // }}
              >
                {/* <strong>Security & Verification Notice:</strong> Your resume is processed privately for your account only and is never published or shared. All extracted credentials will be created as draft claims (unverified). Your existing identity verifications (such as Aadhaar eKYC) remain securely intact. */}
              </div>
            </div>
          )}

          {/* STEP 2: PROCESSING LOADER */}
          {step === "PROCESSING" && (
            <div style={{ padding: "40px 20px", textAlign: "center" }}>
              <LiquidLoader text={processingMsg} />
              <p style={{ marginTop: 24, fontSize: 13, color: C.textLight }}>
                This usually takes 3 to 10 seconds. Please keep this window open.
              </p>
            </div>
          )}

          {/* STEP 3: REVIEW & EDIT DRAFT */}
          {step === "REVIEW" && (
            <div>
              {/* Document Banner */}
              <div
                style={{
                  padding: "12px 16px",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 18 }}>📄</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>{fileName}</div>
                    <div style={{ fontSize: 11.5, color: "#16a34a", fontWeight: 600 }}>
                      ✓ Text extracted successfully • High Confidence ({Math.round(confidence.experience * 100)}%)
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setStep("UPLOAD");
                    setError(null);
                  }}
                  style={{
                    padding: "5px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    background: "#ffffff",
                    border: "1px solid #cbd5e1",
                    borderRadius: 6,
                    color: "#475569",
                    cursor: "pointer",
                  }}
                >
                  ↺ Upload Different Resume
                </button>
              </div>

              {/* Navigation Tabs */}
              <div
                style={{
                  display: "flex",
                  borderBottom: "1px solid #e2e8f0",
                  gap: 4,
                  marginBottom: 18,
                  overflowX: "auto",
                }}
              >
                {[
                  { key: "summary", label: "Summary & Location" },
                  { key: "skills", label: `Skills (${skills.length})` },
                  { key: "experience", label: `Work (${experience.filter((e) => e.selected).length})` },
                  { key: "education", label: `Education (${education.filter((e) => e.selected).length})` },
                  { key: "certifications", label: `Certifications (${certifications.filter((c) => c.selected).length})` },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      padding: "8px 14px",
                      fontSize: 13,
                      fontWeight: activeTab === tab.key ? 700 : 500,
                      color: activeTab === tab.key ? "#057642" : "#64748b",
                      borderBottom: activeTab === tab.key ? "2px solid #057642" : "2px solid transparent",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab 1: Summary & Location */}
              {activeTab === "summary" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <label style={{ fontSize: 13, fontWeight: 700, color: C.navy, display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                          type="checkbox"
                          checked={applyBio}
                          onChange={(e) => setApplyBio(e.target.checked)}
                          style={{ accentColor: "#057642", width: 16, height: 16 }}
                        />
                        Professional Summary / Bio
                      </label>
                      {currentProfile?.bio && (
                        <span style={{ fontSize: 11, color: "#64748b" }}>
                          Current profile has an existing bio
                        </span>
                      )}
                    </div>
                    <textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      disabled={!applyBio}
                      rows={4}
                      placeholder="Write or review your professional summary..."
                      style={{
                        width: "100%",
                        padding: 10,
                        fontSize: 13,
                        borderRadius: 8,
                        border: "1px solid #cbd5e1",
                        fontFamily: font,
                        boxSizing: "border-box",
                        background: applyBio ? "#ffffff" : "#f8fafc",
                        color: applyBio ? C.navy : "#94a3b8",
                      }}
                    />
                  </div>

                  <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <label style={{ fontSize: 13, fontWeight: 700, color: C.navy, display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                          type="checkbox"
                          checked={applyLocation}
                          onChange={(e) => setApplyLocation(e.target.checked)}
                          style={{ accentColor: "#057642", width: 16, height: 16 }}
                        />
                        Location Details
                      </label>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 4 }}>City</div>
                        <input
                          type="text"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          disabled={!applyLocation}
                          placeholder="e.g. Bengaluru"
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            fontSize: 13,
                            borderRadius: 6,
                            border: "1px solid #cbd5e1",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 4 }}>State</div>
                        <input
                          type="text"
                          value={stateVal}
                          onChange={(e) => setStateVal(e.target.value)}
                          disabled={!applyLocation}
                          placeholder="e.g. Karnataka"
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            fontSize: 13,
                            borderRadius: 6,
                            border: "1px solid #cbd5e1",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 2: Skills */}
              {activeTab === "skills" && (
                <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.navy }}>Skills Competencies</h3>
                      <p style={{ margin: "2px 0 0", fontSize: 12, color: C.textMed }}>
                        Add, remove, or modify extracted skills (up to 30)
                      </p>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <label style={{ fontSize: 12, color: C.navy, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="skillsMode"
                          checked={mergeSkills}
                          onChange={() => setMergeSkills(true)}
                          style={{ accentColor: "#057642" }}
                        />
                        Merge with existing
                      </label>
                      <label style={{ fontSize: 12, color: C.navy, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="skillsMode"
                          checked={!mergeSkills}
                          onChange={() => setMergeSkills(false)}
                          style={{ accentColor: "#057642" }}
                        />
                        Replace existing
                      </label>
                    </div>
                  </div>

                  {/* Add skill input */}
                  <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    <input
                      type="text"
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === ",") {
                          e.preventDefault();
                          handleAddSkill();
                        }
                      }}
                      placeholder="Type a skill and press Enter..."
                      style={{
                        flex: 1,
                        padding: "7px 12px",
                        fontSize: 13,
                        borderRadius: 6,
                        border: "1px solid #cbd5e1",
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddSkill}
                      style={{
                        padding: "7px 14px",
                        fontSize: 12.5,
                        fontWeight: 700,
                        background: "#057642",
                        color: "#ffffff",
                        border: "none",
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    >
                      + Add
                    </button>
                  </div>

                  {/* Skills tags */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {skills.map((skill, index) => (
                      <span
                        key={index}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: "#057642",
                          background: "#DCF5E7",
                          border: "1px solid #bbf7d0",
                          borderRadius: 16,
                          padding: "3px 10px",
                        }}
                      >
                        {skill}
                        <button
                          type="button"
                          onClick={() => handleRemoveSkill(index)}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#057642",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 700,
                            padding: 0,
                          }}
                          title="Remove skill"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab 3: Work Experience */}
              {activeTab === "experience" && (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>
                      Work Experience Entries ({experience.length})
                    </span>
                    <button
                      type="button"
                      onClick={handleAddExperience}
                      style={{
                        padding: "5px 12px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#057642",
                        background: "#DCF5E7",
                        border: "1px solid #bbf7d0",
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    >
                      + Add Job Entry
                    </button>
                  </div>

                  {experience.length === 0 ? (
                    <div style={{ padding: 24, textAlign: "center", background: "#f8fafc", borderRadius: 8, color: C.textLight, fontSize: 13 }}>
                      No work experience entries extracted. Click "+ Add Job Entry" to create one.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {experience.map((item) => (
                        <div
                          key={item.id}
                          style={{
                            padding: 14,
                            borderRadius: 10,
                            border: item.selected ? "1.5px solid #93c5fd" : "1px solid #e2e8f0",
                            background: item.selected ? "#ffffff" : "#f8fafc",
                            opacity: item.selected ? 1 : 0.65,
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: C.navy, cursor: "pointer" }}>
                              <input
                                type="checkbox"
                                checked={item.selected}
                                onChange={(e) => handleUpdateExperience(item.id, "selected", e.target.checked)}
                                style={{ accentColor: "#057642", width: 16, height: 16 }}
                              />
                              Include in Profile
                            </label>
                            <button
                              type="button"
                              onClick={() => handleDeleteExperience(item.id)}
                              style={{ background: "none", border: "none", color: "#dc2626", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                            >
                              Delete
                            </button>
                          </div>

                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: C.textMed, marginBottom: 2 }}>Job Title *</div>
                              <input
                                type="text"
                                value={item.title}
                                onChange={(e) => handleUpdateExperience(item.id, "title", e.target.value)}
                                placeholder="e.g. Senior Software Engineer"
                                style={{ width: "100%", padding: "6px 8px", fontSize: 12.5, borderRadius: 6, border: "1px solid #cbd5e1", boxSizing: "border-box" }}
                              />
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: C.textMed, marginBottom: 2 }}>Employer / Organisation *</div>
                              <input
                                type="text"
                                value={item.issuer}
                                onChange={(e) => handleUpdateExperience(item.id, "issuer", e.target.value)}
                                placeholder="e.g. Google India"
                                style={{ width: "100%", padding: "6px 8px", fontSize: 12.5, borderRadius: 6, border: "1px solid #cbd5e1", boxSizing: "border-box" }}
                              />
                            </div>
                          </div>

                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: C.textMed, marginBottom: 2 }}>Start Date (YYYY-MM)</div>
                              <input
                                type="text"
                                value={item.startDate}
                                onChange={(e) => handleUpdateExperience(item.id, "startDate", e.target.value)}
                                placeholder="2021-06"
                                style={{ width: "100%", padding: "6px 8px", fontSize: 12.5, borderRadius: 6, border: "1px solid #cbd5e1", boxSizing: "border-box" }}
                              />
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: C.textMed, marginBottom: 2 }}>End Date (YYYY-MM)</div>
                              <input
                                type="text"
                                value={item.endDate}
                                disabled={item.isCurrent}
                                onChange={(e) => handleUpdateExperience(item.id, "endDate", e.target.value)}
                                placeholder="2023-12"
                                style={{ width: "100%", padding: "6px 8px", fontSize: 12.5, borderRadius: 6, border: "1px solid #cbd5e1", boxSizing: "border-box", background: item.isCurrent ? "#f1f5f9" : "#ffffff" }}
                              />
                            </div>
                            <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 6 }}>
                              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.navy, cursor: "pointer" }}>
                                <input
                                  type="checkbox"
                                  checked={item.isCurrent}
                                  onChange={(e) => handleUpdateExperience(item.id, "isCurrent", e.target.checked)}
                                  style={{ accentColor: "#057642" }}
                                />
                                Currently work here
                              </label>
                            </div>
                          </div>

                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: C.textMed, marginBottom: 2 }}>Description</div>
                            <textarea
                              value={item.description}
                              onChange={(e) => handleUpdateExperience(item.id, "description", e.target.value)}
                              rows={2}
                              placeholder="Key responsibilities and achievements..."
                              style={{ width: "100%", padding: "6px 8px", fontSize: 12, borderRadius: 6, border: "1px solid #cbd5e1", boxSizing: "border-box", fontFamily: font }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Tab 4: Education */}
              {activeTab === "education" && (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>
                      Education Entries ({education.length})
                    </span>
                    <button
                      type="button"
                      onClick={handleAddEducation}
                      style={{
                        padding: "5px 12px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#057642",
                        background: "#DCF5E7",
                        border: "1px solid #bbf7d0",
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    >
                      + Add Education Entry
                    </button>
                  </div>

                  {education.length === 0 ? (
                    <div style={{ padding: 24, textAlign: "center", background: "#f8fafc", borderRadius: 8, color: C.textLight, fontSize: 13 }}>
                      No education entries extracted. Click "+ Add Education Entry" to add your degree.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {education.map((item) => (
                        <div
                          key={item.id}
                          style={{
                            padding: 14,
                            borderRadius: 10,
                            border: item.selected ? "1.5px solid #93c5fd" : "1px solid #e2e8f0",
                            background: item.selected ? "#ffffff" : "#f8fafc",
                            opacity: item.selected ? 1 : 0.65,
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: C.navy, cursor: "pointer" }}>
                              <input
                                type="checkbox"
                                checked={item.selected}
                                onChange={(e) => handleUpdateEducation(item.id, "selected", e.target.checked)}
                                style={{ accentColor: "#057642", width: 16, height: 16 }}
                              />
                              Include in Profile
                            </label>
                            <button
                              type="button"
                              onClick={() => handleDeleteEducation(item.id)}
                              style={{ background: "none", border: "none", color: "#dc2626", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                            >
                              Delete
                            </button>
                          </div>

                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: C.textMed, marginBottom: 2 }}>Degree / Qualification *</div>
                              <input
                                type="text"
                                value={item.title}
                                onChange={(e) => handleUpdateEducation(item.id, "title", e.target.value)}
                                placeholder="e.g. B.Tech Computer Science"
                                style={{ width: "100%", padding: "6px 8px", fontSize: 12.5, borderRadius: 6, border: "1px solid #cbd5e1", boxSizing: "border-box" }}
                              />
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: C.textMed, marginBottom: 2 }}>Institution / University *</div>
                              <input
                                type="text"
                                value={item.issuer}
                                onChange={(e) => handleUpdateEducation(item.id, "issuer", e.target.value)}
                                placeholder="e.g. IIT Delhi"
                                style={{ width: "100%", padding: "6px 8px", fontSize: 12.5, borderRadius: 6, border: "1px solid #cbd5e1", boxSizing: "border-box" }}
                              />
                            </div>
                          </div>

                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: C.textMed, marginBottom: 2 }}>Start Date (YYYY-MM)</div>
                              <input
                                type="text"
                                value={item.startDate}
                                onChange={(e) => handleUpdateEducation(item.id, "startDate", e.target.value)}
                                placeholder="2016-08"
                                style={{ width: "100%", padding: "6px 8px", fontSize: 12.5, borderRadius: 6, border: "1px solid #cbd5e1", boxSizing: "border-box" }}
                              />
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: C.textMed, marginBottom: 2 }}>End Date (YYYY-MM)</div>
                              <input
                                type="text"
                                value={item.endDate}
                                onChange={(e) => handleUpdateEducation(item.id, "endDate", e.target.value)}
                                placeholder="2020-05"
                                style={{ width: "100%", padding: "6px 8px", fontSize: 12.5, borderRadius: 6, border: "1px solid #cbd5e1", boxSizing: "border-box" }}
                              />
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: C.textMed, marginBottom: 2 }}>Grade / CGPA</div>
                              <input
                                type="text"
                                value={item.grade}
                                onChange={(e) => handleUpdateEducation(item.id, "grade", e.target.value)}
                                placeholder="e.g. 8.5 CGPA"
                                style={{ width: "100%", padding: "6px 8px", fontSize: 12.5, borderRadius: 6, border: "1px solid #cbd5e1", boxSizing: "border-box" }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Tab 5: Certifications */}
              {activeTab === "certifications" && (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>
                      Certifications & Licenses ({certifications.length})
                    </span>
                    <button
                      type="button"
                      onClick={handleAddCertification}
                      style={{
                        padding: "5px 12px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#057642",
                        background: "#DCF5E7",
                        border: "1px solid #bbf7d0",
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    >
                      + Add Certification
                    </button>
                  </div>

                  {certifications.length === 0 ? (
                    <div style={{ padding: 24, textAlign: "center", background: "#f8fafc", borderRadius: 8, color: C.textLight, fontSize: 13 }}>
                      No certifications extracted. Click "+ Add Certification" to add one.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {certifications.map((item) => (
                        <div
                          key={item.id}
                          style={{
                            padding: 14,
                            borderRadius: 10,
                            border: item.selected ? "1.5px solid #93c5fd" : "1px solid #e2e8f0",
                            background: item.selected ? "#ffffff" : "#f8fafc",
                            opacity: item.selected ? 1 : 0.65,
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: C.navy, cursor: "pointer" }}>
                              <input
                                type="checkbox"
                                checked={item.selected}
                                onChange={(e) => handleUpdateCertification(item.id, "selected", e.target.checked)}
                                style={{ accentColor: "#057642", width: 16, height: 16 }}
                              />
                              Include in Profile
                            </label>
                            <button
                              type="button"
                              onClick={() => handleDeleteCertification(item.id)}
                              style={{ background: "none", border: "none", color: "#dc2626", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                            >
                              Delete
                            </button>
                          </div>

                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: C.textMed, marginBottom: 2 }}>Certificate Name *</div>
                              <input
                                type="text"
                                value={item.title}
                                onChange={(e) => handleUpdateCertification(item.id, "title", e.target.value)}
                                placeholder="AWS Certified Architect"
                                style={{ width: "100%", padding: "6px 8px", fontSize: 12.5, borderRadius: 6, border: "1px solid #cbd5e1", boxSizing: "border-box" }}
                              />
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: C.textMed, marginBottom: 2 }}>Issuing Body *</div>
                              <input
                                type="text"
                                value={item.issuer}
                                onChange={(e) => handleUpdateCertification(item.id, "issuer", e.target.value)}
                                placeholder="Amazon Web Services"
                                style={{ width: "100%", padding: "6px 8px", fontSize: 12.5, borderRadius: 6, border: "1px solid #cbd5e1", boxSizing: "border-box" }}
                              />
                            </div>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 600, color: C.textMed, marginBottom: 2 }}>Issue Date (YYYY-MM)</div>
                              <input
                                type="text"
                                value={item.startDate}
                                onChange={(e) => handleUpdateCertification(item.id, "startDate", e.target.value)}
                                placeholder="2023-03"
                                style={{ width: "100%", padding: "6px 8px", fontSize: 12.5, borderRadius: 6, border: "1px solid #cbd5e1", boxSizing: "border-box" }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* STEP 4: SUCCESS CONFIRMATION */}
          {step === "SUCCESS" && (
            <div style={{ padding: "30px 20px", textAlign: "center" }}>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "#DCF5E7",
                  color: "#057642",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                  fontSize: 26,
                }}
              >
                ✓
              </div>
              <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700, color: C.navy }}>
                Profile Successfully Updated!
              </h3>
              <p style={{ margin: "0 0 20px", fontSize: 13, color: C.textMed, lineHeight: 1.5 }}>
                Your resume information has been added to your profile. All new credentials were created as draft claims (status NONE) and are ready for official verification.
              </p>

              {appliedStats && (
                <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
                  <div style={{ padding: "8px 14px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                    <span style={{ fontWeight: 700, color: "#057642" }}>{appliedStats.experienceCount}</span> Jobs Added
                  </div>
                  <div style={{ padding: "8px 14px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                    <span style={{ fontWeight: 700, color: "#057642" }}>{appliedStats.educationCount}</span> Degrees Added
                  </div>
                  <div style={{ padding: "8px 14px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                    <span style={{ fontWeight: 700, color: "#057642" }}>{appliedStats.certificationsCount}</span> Certifications Added
                  </div>
                  <div style={{ padding: "8px 14px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                    <span style={{ fontWeight: 700, color: "#057642" }}>{appliedStats.skillsCount}</span> Skills Updated
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: "10px 24px",
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#ffffff",
                  background: "#057642",
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(5, 118, 66, 0.25)",
                }}
              >
                Done & Return to Profile
              </button>
            </div>
          )}
        </div>

        {/* Modal Footer (Review Step Only) */}
        {step === "REVIEW" && (
          <div
            style={{
              padding: "14px 24px",
              borderTop: "1px solid #e2e8f0",
              background: "#f8fafc",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              style={{
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: 600,
                color: "#64748b",
                background: "#ffffff",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                cursor: isSubmitting ? "not-allowed" : "pointer",
              }}
            >
              Discard & Close
            </button>

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 12, color: C.textLight }}>
                Ready to apply: {experience.filter((e) => e.selected).length} jobs, {education.filter((e) => e.selected).length} degrees, {skills.length} skills
              </span>
              <button
                type="button"
                onClick={handleSubmitReview}
                disabled={isSubmitting}
                style={{
                  padding: "9px 20px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#ffffff",
                  background: isSubmitting ? "#64748b" : "#057642",
                  border: "none",
                  borderRadius: 8,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  boxShadow: "0 4px 12px rgba(5, 118, 66, 0.25)",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {isSubmitting ? (
                  <>
                    <span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid #ffffff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.6s linear infinite" }} />
                    Applying...
                  </>
                ) : (
                  <>
                    <span>Confirm & Apply to Profile</span>
                    <span style={{ fontSize: 15 }}>→</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}